import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import shippingZoneService from '../../services/shippingZoneService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import ShippingZoneDialog from './ShippingZoneDialog';

const ShippingZonesIndex = () => {
  const { isRtl } = useLocalization();
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogZone, setDialogZone] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    loadZones();
  }, []);

  const loadZones = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await shippingZoneService.getAll();
      setZones(data);
    } catch (err) {
      console.error('Error loading shipping zones:', err);
      setError(isRtl ? 'حدث خطأ أثناء تحميل أجور التوصيل' : 'Failed to load shipping zones');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setDialogZone(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (zone) => {
    setDialogZone(zone);
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);

  const handleDialogSuccess = () => {
    setIsDialogOpen(false);
    loadZones();
  };

  const columns = [
    { header: isRtl ? 'المحافظة (عربي)' : 'Governorate (Arabic)', accessor: 'nameAr' },
    { header: isRtl ? 'المحافظة (إنجليزي)' : 'Governorate (English)', accessor: 'nameEn' },
    {
      header: isRtl ? 'أجور التوصيل (د.ع)' : 'Shipping Fee (IQD)',
      accessor: 'price',
      render: (value) => `${value.toLocaleString(isRtl ? 'ar' : 'en')} ${isRtl ? 'د.ع' : 'IQD'}`,
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (_id, row) => (
        <Button size="small" variant="primary" onClick={() => openEditDialog(row)}>
          {isRtl ? 'تعديل' : 'Edit'}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading shipping zones...'} />;
  }

  return (
    <div className="shipping-zones-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'أجور التوصيل' : 'Shipping Zones'}</h1>
            <p className="page-description">
              {isRtl
                ? 'تحديد أجور التوصيل الخاصة بكل محافظة. هذا السعر هو المستخدم فعلياً عند حساب الطلبات، وهو نفس ما يظهر للزبون في الموقع والتطبيق.'
                : 'Set the delivery fee for each governorate. This is the price actually charged on checkout, and the same one shown to customers on the website and app.'}
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={openCreateDialog}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {isRtl ? 'إضافة محافظة' : 'Add Governorate'}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">{error}</div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={zones}
            emptyMessage={isRtl ? 'لا توجد محافظات' : 'No shipping zones found'}
          />
        </div>
      </div>

      <ShippingZoneDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onSuccess={handleDialogSuccess}
        zone={dialogZone}
      />
    </div>
  );
};

export default ShippingZonesIndex;
